import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Timer } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { track } from '@/lib/tracking';
import { formatCountdown, useMembersFirst } from '@/lib/membersFirst';

/**
 * "Members first" badge with a live countdown. Display only — the rule is
 * enforced server-side in mkt_claim_lead_core (reason 'members_first').
 * Renders NOTHING while the window is disabled (the default) or once it ends.
 *
 *   card    compact pill for JobCard
 *   detail  panel for the job detail sidebar; non-members get a plans link
 */
interface Props {
  variant?: 'card' | 'detail';
  listingId?: string;
  createdAt?: string | null;
  className?: string;
}

const MembersFirstBadge: React.FC<Props> = ({ variant = 'card', listingId, createdAt, className = '' }) => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const mf = useMembersFirst({ listingId: variant === 'detail' ? listingId : undefined, createdAt });
  if (!mf.active) return null;
  const left = formatCountdown(mf.remainingMs);

  if (variant === 'card') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-900 ring-1 ring-amber-300 ${className}`}
        title={es ? 'Solo miembros activos pueden desbloquear este proyecto por ahora' : 'Only active members can unlock this project for now'}
      >
        <Crown className="h-3 w-3" /> {es ? 'Miembros primero' : 'Members first'} · <Timer className="h-3 w-3" /> {left}
      </span>
    );
  }

  return (
    <div className={`rounded-xl border-2 border-amber-300 bg-amber-50 p-4 ${className}`} role="status">
      <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
        <Crown className="h-4 w-4" /> {es ? 'Miembros primero' : 'Members first'}
      </p>
      <p className="mt-1 flex items-center gap-1.5 text-2xl font-extrabold tabular-nums text-amber-900">
        <Timer className="h-5 w-5" /> {left}
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-amber-800">
        {mf.viewerIsMember
          ? es
            ? 'Usted es miembro: puede desbloquear este proyecto ahora, antes que los demás.'
            : 'You are a member: you can unlock this project now, before everyone else.'
          : es
          ? 'Por ahora solo los miembros activos pueden desbloquear este proyecto. Se abre para todos cuando termine el contador.'
          : 'Only active members can unlock this project right now. It opens to everyone when the timer ends.'}
      </p>
      {!mf.viewerIsMember && (
        <Link
          to={path('/pricing')}
          onClick={() => track('cta_click', { action: 'members_first_see_plans', listingId })}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-600"
        >
          {es ? 'Hágase miembro para desbloquear ahora' : 'Become a member to unlock now'}
        </Link>
      )}
    </div>
  );
};

export default MembersFirstBadge;
